import { useFontRender } from "@/components/FontRender";
import FormatedText from "@/components/FormatedText";
import {
  autoLinksPrefix,
  autoLinksValidations,
} from "@/components/microsite/links";
import { useComponent } from "@/hooks/useMicrositeComponent";
import { IMicrositeComponent, MComponentImageProps } from "@/types/microsite";

import { useAPI } from "@/lib/api";
import { formatToText } from "@/lib/formatToText";
import assetUrl from "@/vars/assetUrl";
import shortUrl from "@/vars/shortUrl";
import { cn } from "@/lib/utils";
import { Fragment, useEffect, useMemo, useState } from "react";

import { SpoilerButton } from "./Spoiler";
import { TextComponentStyle } from "./Text";

const ImageComponentStyle = ({
  imageStyle,
  imagePlacement,
  imageWidthConfig,
  imageWidth,
  isSpoiler,
  noPadding,
  id,
  children,
}) => {
  return (
    <div
      id={id}
      className={cn(
        "relative w-full flex justify-center",
        !noPadding ? "p-2" : "",
        imageStyle === "col-2" && "w-6/12",
        imageStyle === "col-3" && "w-4/12",
        imagePlacement === "full-width" && "block",
        imageWidthConfig === "custom" && "flex justify-center",
        parseInt(imageWidth) <= 0 && "hidden"
      )}
      style={{ minHeight: "100px" }}
    >
      {children}
    </div>
  );
};

const StyledImage = ({ imagePlacement, ...props }) => {
  return (
    <img
      src={props.src}
      className={cn([
        imagePlacement === "full-width" && "w-full",
        imagePlacement?.startsWith("square") && "hidden",
        imagePlacement?.startsWith("rounded") && "hidden",
      ])}
      alt={props.alt}
    />
  );
};

const StyledImageFigure = ({ imagePlacement, image, ...props }) => {
  const { focusTo } = useComponent();

  const style = useMemo(() => {
    const style: any = props.style ? props.style : {};
    if (
      imagePlacement?.startsWith("rounded-") ||
      imagePlacement?.startsWith("square-")
    ) {
      style.backgroundImage = `url("${assetUrl + image}")`;
    }
    return style;
  }, [props.style, imagePlacement, image]);

  return (
    <figure
      onClick={() => {
        if (props.isBuilder) {
          focusTo(props.id);
        }
      }}
      className={cn(
        "flex justify-center",
        imagePlacement?.startsWith("square-") && "relative bg-gray-400 bg-no-repeat bg-center w-full",
        imagePlacement === "square-contain" && "bg-contain",
        imagePlacement === "square-cover" && "bg-cover",
        imagePlacement?.startsWith("rounded-") && "bg-gray-400 rounded-full overflow-hidden bg-cover bg-no-repeat bg-center",
        imagePlacement === "rounded-contain" && "bg-contain"
      )}
      style={{
        ...style,
        ...(imagePlacement?.startsWith("square-") || imagePlacement?.startsWith("rounded-") ? { paddingBottom: "100%" } : {})
      }}
    >
      {props.children}
    </figure>
  );
};

const ImageComponent = {
  kind: "IMAGE",
  Render: (props) => {
    const api = useAPI();
    const [isOpen, setIsOpen] = useState(false);
    const {
      imagePlacement,
      imageStyle,
      imageWidth,
      imageWidthConfig,
      textColor,
      textFont,
      textPosition,
      textSize,
      textSizeCustom,
      isSpoiler: _isSpoiler,
      spoilerButtonColor,
      spoilerButtonTextPosition,
      spoilerButtonText,
      spoilerButtonIcon,
      spoilerButtonFont,
      needPassword,
      passwordMessage,
      keepInPage,
    } = props.data;

    const [{ content, href, image, value, linkType, valueSecondary }, setData] =
      useState({
        content: props.data.content,
        href: props.data.href,
        image: props.data.image,
        value: props.data.value,
        linkType: props.data.linkType,
        valueSecondary: props.data.valueSecondary,
      });

    const isSpoiler = _isSpoiler && !imageStyle?.startsWith("col-");

    const getLink = ({ href, value, linkType, valueSecondary }) => {
      if (linkType !== "link") {
        const validate = autoLinksValidations[linkType];
        if (validate && validate(value)) {
          let text = autoLinksPrefix[linkType] + value;

          if (linkType === "whatsapp") {
            if (valueSecondary) {
              text += "?text=" + encodeURIComponent(valueSecondary);
            }
          }

          return text;
        }
      }
      return href;
    };

    const realLink = useMemo(() => {
      return getLink({ href, value, linkType, valueSecondary });
    }, [href, value, linkType, valueSecondary]);

    const imageWidthStyle = useMemo(() => {
      const styles: any = {};
      if (
        !imageStyle?.startsWith("col-") &&
        (imagePlacement?.startsWith("rounded-") ||
          imagePlacement?.startsWith("square-"))
      ) {
        const w = parseInt(imageWidth) || 40;
        styles.width = `${w <= 0 ? 0 : w > 40 ? 40 : w}%`;
      } else if (imageWidth && imageWidthConfig === "custom") {
        styles.width = `${parseInt(imageWidth) <= 0 ? 0 : imageWidth}%`;
      } else {
        styles.width = "100%";
      }
      return styles;
    }, [imageWidth, imageWidthConfig, imagePlacement, imageStyle]);

    const { fontStyle, RenderFontStyle } = useFontRender({
      textFont,
      style: { color: textColor ? textColor : undefined },
    });

    const isKeepInPage = useMemo(() => {
      return href && href.startsWith(shortUrl) && keepInPage;
    }, [href, keepInPage]);

    const track = (e) => {
      if (props.isBuilder) {
        e.preventDefault();
        return;
      }
      const post = {
        component_id: props.id,
        action_type: "image",
      };
      api.post("/track/microsite", post).then(() => {
        //...
      });
    };

    const onProtectedOpen = (data) => {
      setData({
        content: data.content,
        href: data.href,
        image: data.image,
        linkType: data.linkType,
        value: data.value,
        valueSecondary: data.valueSecondary,
      });
    };

    useEffect(() => {
      setData({
        content: props.data.content,
        href: props.data.href,
        image: props.data.image,
        linkType: props.data.linkType,
        value: props.data.value,
        valueSecondary: props.data.valueSecondary,
      });
    }, [props.data]);

    if (!image) {
      return <Fragment />;
    }

    const ImageContent = (
      <StyledImageFigure
        {...props.data}
        id={props.id}
        isBuilder={props.isBuilder}
      >
        <StyledImage
          {...props.data}
          src={assetUrl + image}
          alt={formatToText(props.data.content)}
        />
      </StyledImageFigure>
    );

    return (
      <>
        {RenderFontStyle}
        <SpoilerButton
          needPassword={needPassword}
          spoilerButtonFont={spoilerButtonFont}
          microsite={props.microsite}
          spoilerButtonColor={spoilerButtonColor}
          spoilerButtonIcon={spoilerButtonIcon}
          spoilerButtonText={spoilerButtonText}
          spoilerButtonTextPosition={spoilerButtonTextPosition}
          isOpen={isOpen}
          isSpoiler={isSpoiler}
          onToggle={() => setIsOpen(!isOpen)}
          isBuilder={props.isBuilder}
          id={props.id}
          passwordMessage={passwordMessage}
          onProtectedOpen={onProtectedOpen}
          isFocus={props.isFocus}
        >
          <ImageComponentStyle
            {...props.data}
            isSpoiler={isSpoiler}
            noPadding={props.data.noPadding}
            id={props.isBuilder && !isSpoiler ? `mcomp-${props.id}` : undefined}
          >
            <div style={imageWidthStyle}>
              {realLink ? (
                <a
                  href={realLink}
                  onClick={track}
                  target={isKeepInPage ? undefined : "_blank"}
                  rel="noopener noreferrer"
                  className="block w-full transition hover:brightness-75"
                >
                  {ImageContent}
                </a>
              ) : (
                ImageContent
              )}
              {content?.length > 0 ? (
                <div className="-mx-2 mt-1">
                  <TextComponentStyle
                    position={textPosition}
                    style={fontStyle}
                    textSize={textSize}
                    //@ts-ignore
                    textSizeCustom={textSizeCustom}
                  >
                    <FormatedText value={content} />
                  </TextComponentStyle>
                </div>
              ) : (
                <Fragment />
              )}
            </div>
          </ImageComponentStyle>
        </SpoilerButton>
      </>
    );
  },
} as IMicrositeComponent<MComponentImageProps>;

export default ImageComponent;
